import { useCallback, useEffect, useState } from "react";
import {
  braintree as createBraintreeProcessor,
  stripe as createStripeProcessor,
} from "./processors";
import { PaymentProcessorFailedInitializationError } from "../errors";
import {
  HostedFieldProcessorInterface,
  HostedFieldType,
  HostedFieldValue,
  PaymentProcessor,
} from "@zapier/payment/esm/types";

import { Colors, useTheme } from "@zapier/design-system";

const legacyHostedFieldStyle = {
  // Custom font only works with Stripe for now as Braintree does not support it.
  // I've revived an issue on their side as technically they could support it but their
  // justification is vague:
  //   https://github.com/braintree/braintree-web/issues/75#issuecomment-441748583
  fontFamily: '"Open Sans", Helvetica, arial, sans-serif',
  fontSize: "16px",
  color: "#5f6c72",
  "::placeholder": {
    color: "#969ea2",
  },
};

const rebrandHostedFieldStyle = {
  fontFamily: '"Inter", "Open Sans", Helvetica, arial, sans-serif',
  color: Colors.neutral800,
  fontSize: "16px",
  fontWeight: 500,
  "::placeholder": {
    color: Colors.neutral600,
  },
};

export const usePaymentProcessor = (
  paymentProcessor: PaymentProcessor,
  apiKey: string,
  onChange: (name: HostedFieldType, value: HostedFieldValue) => void
): [
  HostedFieldProcessorInterface | undefined | null,
  PaymentProcessorFailedInitializationError | undefined | null,
  () => Promise<void>
] => {
  const { isRebrand } = useTheme();

  const HostedFieldStyle = isRebrand
    ? rebrandHostedFieldStyle
    : legacyHostedFieldStyle;

  const [processor, setProcessor] =
    useState<HostedFieldProcessorInterface | null>(null);

  const [error, setError] =
    useState<PaymentProcessorFailedInitializationError | null>(null);
  /** Step 1. Initialize the payment processor, taking care to capture and show any errors resulting from this */
  useEffect(() => {
    let isMounted = true;
    const init = async () => {
      if (paymentProcessor === PaymentProcessor.stripe) {
        const result = await createStripeProcessor();
        if (!isMounted) return;
        if (result.isOk()) {
          setProcessor(result.value);
        } else {
          setError(
            new PaymentProcessorFailedInitializationError(paymentProcessor)
          );
        }
        // This whole code section 👇 can likely go away, we don't have Braintree for cards anymore
      } else {
        try {
          const initializedProcessor = await createBraintreeProcessor(apiKey);
          setProcessor(initializedProcessor);
        } catch (err) {
          setError(
            new PaymentProcessorFailedInitializationError(paymentProcessor)
          );
        }
      }
    };

    init();

    return () => {
      isMounted = false;
    };
  }, [paymentProcessor, apiKey]);

  /** Step 2. Once the payment processor is initialized, create the hosted fields so they can receive input */
  const initializeHostedFields = useCallback(async () => {
    if (!processor) return;
    try {
      await processor.createFields(HostedFieldStyle, onChange);
    } catch (err) {
      // If the user switches to another page or to Paypal quickly
      // an error may be thrown here because the DOM elements won't exist anymore.
      // In that case we silently fail here.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processor]);

  useEffect(() => {
    if (!processor) return;

    initializeHostedFields();

    // eslint-disable-next-line consistent-return
    return () => {
      /** Step 3. Destroy the fields and un-initialize the processor whenever we unmount/change the processor */
      processor.destroyFields();
      setProcessor(null);
    };
  }, [initializeHostedFields, processor]);

  return [processor, error, initializeHostedFields];
};
